import type { ConsoleMenu } from '@/features/settings/types';

interface MenuGroup {
  top: ConsoleMenu;
  subs: ConsoleMenu[];
}

export class MenuGroups {
  /** 대메뉴(parent 없음) 하나에 그 밑의 개별 화면(parent가 그 대메뉴 키인 것)을 묶는다. */
  static of(menus: readonly ConsoleMenu[]): MenuGroup[] {
    return menus
      .filter((menu) => !menu.parent)
      .map((top) => ({ top, subs: menus.filter((menu) => menu.parent === top.key) }));
  }
}

/**
 * 메뉴 체크박스 묶음. `locked`에 든 메뉴는 이미 다른 경로(팀 기본 권한)로 받고 있어 켜진 채 잠긴다.
 * `parent`가 있는 메뉴(대메뉴 안의 개별 화면)는 그 대메뉴 줄 옆에 이어서 그린다 — 대메뉴 권한과는
 * 독립된 선택이지만, 어느 대메뉴 소속인지는 보여야 한다.
 */
export function MenuPicker({
  menus,
  picked,
  onChange,
  locked = [],
}: {
  menus: ConsoleMenu[];
  picked: string[];
  onChange: (next: string[]) => void;
  locked?: string[];
}): React.JSX.Element {
  const toggle = (key: string): void =>
    onChange(picked.includes(key) ? picked.filter((item) => item !== key) : [...picked, key]);

  const checkbox = (menu: ConsoleMenu): React.JSX.Element => {
    const fromTeam = locked.includes(menu.key);
    return (
      <label key={menu.key} className={`flex items-center gap-1.5 whitespace-nowrap text-[12px] ${fromTeam ? 'text-faint' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          checked={fromTeam || picked.includes(menu.key)}
          disabled={fromTeam}
          onChange={() => toggle(menu.key)}
        />
        {menu.label}
        {fromTeam && <span className="text-[10.5px]">(팀 기본)</span>}
      </label>
    );
  };

  return (
    <div className="flex flex-col gap-1.5">
      {MenuGroups.of(menus).map(({ top, subs }) => (
        // 대메뉴 칸 폭을 고정해 개별 화면 줄의 시작점을 세로로 맞춘다. 가장 긴 라벨("Slack 봇 AI 사용량 (팀 기본)")이
        // 한 줄에 들어가는 폭이어야 한다 — 좁으면 라벨이 칸 안에서 꺾여 줄 높이가 들쭉날쭉해진다.
        <div key={top.key} className="flex items-start gap-x-3">
          <span className="w-[200px] shrink-0">{checkbox(top)}</span>
          {subs.length > 0 && (
            <span className="flex flex-wrap gap-x-3 gap-y-1 border-l border-line pl-3">
              {subs.map((sub) => checkbox(sub))}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
